import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { OutwriteApi } from '../services/OutwriteService';
import OutwriteCollapsibleTable from './OutwriteCollapsibleTable';

const SELECTED_ROW_BG = '#e3f2fd';
const RUN_TIMEOUT_MS = 15 * 60 * 1000;
export const OUTWRITE_TESTS_LOAD_KEY = 'outwrite.testsLoad';

function verdictColor(verdict) {
  const v = String(verdict || '').toLowerCase();
  if (v === 'pass') return 'success.main';
  if (v === 'fail') return 'error.main';
  if (v === 'inconsistent') return 'warning.main';
  return 'text.primary';
}

function invertPassFail(value) {
  return String(value || '').toUpperCase() === 'PASS' ? 'FAIL' : 'PASS';
}

/** TASKS-style Expected=true means etalon equals frozen actual. */
function annotationDraftFromRubrics(rubrics) {
  const next = {};
  (Array.isArray(rubrics) ? rubrics : []).forEach((row) => {
    if (!row?.rubric_id) return;
    const actual = String(row.actual || '').toUpperCase();
    const expected = String(row.expected || '').toUpperCase();
    next[row.rubric_id] = {
      expectedOk: !actual || expected === actual,
      comment: row.comment ?? '',
    };
  });
  return next;
}

/**
 * Author TESTS page: catalog select, run, history, load into TASKS.
 */
export default function OutwriteTests() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [runLoading, setRunLoading] = useState(false);
  const [titleEdits, setTitleEdits] = useState({});
  const [saveTitleId, setSaveTitleId] = useState('');
  const [selectedCatalogId, setSelectedCatalogId] = useState('');
  /** Per rubric_id: { expectedOk: boolean, comment: string } for selected catalog case. */
  const [catalogAnnotations, setCatalogAnnotations] = useState({});
  const [catalogSaveLoading, setCatalogSaveLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadCases = useCallback(() => {
    setCasesLoading(true);
    OutwriteApi.listTestCases({ limit: 500 })
      .then((list) => {
        setCases(Array.isArray(list) ? list : []);
      })
      .catch(() => setCases([]))
      .finally(() => setCasesLoading(false));
  }, []);

  const loadRuns = useCallback(() => {
    setRunsLoading(true);
    OutwriteApi.listTestRuns({ limit: 50 })
      .then((list) => {
        setRuns(Array.isArray(list) ? list : []);
      })
      .catch(() => setRuns([]))
      .finally(() => setRunsLoading(false));
  }, []);

  useEffect(() => {
    loadCases();
    loadRuns();
  }, [loadCases, loadRuns]);

  useEffect(() => {
    if (!selectedRunId) {
      setSelectedRun(null);
      setSelectedCaseId('');
      return undefined;
    }
    let cancelled = false;
    setRunDetailLoading(true);
    OutwriteApi.getTestRun(selectedRunId)
      .then((data) => {
        if (cancelled) return;
        setSelectedRun(data);
        const first = data?.cases?.[0];
        setSelectedCaseId(first ? String(first.id) : '');
      })
      .catch(() => {
        if (!cancelled) setSelectedRun(null);
      })
      .finally(() => {
        if (!cancelled) setRunDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRunId]);

  const allSelected =
    cases.length > 0 && cases.every((row) => selectedIds.has(String(row.id)));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(cases.map((row) => String(row.id))));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRun = () => {
    const ids = [...selectedIds];
    if (!ids.length || runLoading) return;
    setRunLoading(true);
    setMessage(null);
    OutwriteApi.createTestRun(ids, RUN_TIMEOUT_MS)
      .then((run) => {
        setMessage(
          `Run #${run.id}: pass ${run.passCount} / fail ${run.failCount} / inconsistent ${run.inconsistentCount}`,
        );
        setSelectedRunId(String(run.id));
        setSelectedRun(run);
        loadRuns();
      })
      .catch(() => setMessage('Run failed'))
      .finally(() => setRunLoading(false));
  };

  const handleSaveTitle = (id) => {
    const title = titleEdits[id];
    if (title === undefined || saveTitleId) return;
    setSaveTitleId(id);
    OutwriteApi.updateTestCase(id, { title })
      .then((updated) => {
        setCases((prev) =>
          prev.map((row) => (String(row.id) === String(id) ? updated : row)),
        );
        setTitleEdits((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        if (String(selectedCatalogId) === String(id)) {
          setCatalogAnnotations(annotationDraftFromRubrics(updated.rubrics));
        }
      })
      .catch(() => {})
      .finally(() => setSaveTitleId(''));
  };

  const selectCatalogCase = (id) => {
    const catalogId = String(id);
    setSelectedCatalogId(catalogId);
    const row = cases.find((item) => String(item.id) === catalogId);
    setCatalogAnnotations(annotationDraftFromRubrics(row?.rubrics));
  };

  const handleCatalogExpected = (rubricId, expectedOk) => {
    setCatalogAnnotations((prev) => ({
      ...prev,
      [rubricId]: {
        expectedOk: !!expectedOk,
        comment: prev[rubricId]?.comment ?? '',
      },
    }));
  };

  const handleCatalogComment = (rubricId, comment) => {
    setCatalogAnnotations((prev) => ({
      ...prev,
      [rubricId]: {
        expectedOk: prev[rubricId]?.expectedOk !== false,
        comment,
      },
    }));
  };

  const handleSaveCatalogAnnotations = () => {
    const row = cases.find(
      (item) => String(item.id) === String(selectedCatalogId),
    );
    if (!row || catalogSaveLoading) return;
    const rubrics = (Array.isArray(row.rubrics) ? row.rubrics : []).map(
      (item) => {
        const draft = catalogAnnotations[item.rubric_id] || {
          expectedOk: true,
          comment: '',
        };
        const actual = String(item.actual || '').toUpperCase();
        const expected =
          draft.expectedOk !== false
            ? actual || String(item.expected || '').toUpperCase()
            : actual
              ? invertPassFail(actual)
              : invertPassFail(item.expected);
        return {
          rubric_id: item.rubric_id,
          expected,
          comment: draft.comment ?? '',
        };
      },
    );
    setCatalogSaveLoading(true);
    OutwriteApi.updateTestCase(row.id, { rubrics })
      .then((updated) => {
        setCases((prev) =>
          prev.map((item) =>
            String(item.id) === String(updated.id) ? updated : item,
          ),
        );
        setCatalogAnnotations(annotationDraftFromRubrics(updated.rubrics));
        setMessage(`Test case #${updated.id} Expected/Comment saved`);
      })
      .catch(() => setMessage('Failed to save test case annotations'))
      .finally(() => setCatalogSaveLoading(false));
  };

  const handleLoadIntoTasks = (row) => {
    const taskId = row?.taskId;
    if (!taskId) return;
    try {
      sessionStorage.setItem(
        OUTWRITE_TESTS_LOAD_KEY,
        JSON.stringify({
          taskId,
          answerText: row.answerText || '',
        }),
      );
    } catch {
      /* ignore quota */
    }
    navigate('/outwrite/tasks');
  };

  const runCases = useMemo(
    () => (Array.isArray(selectedRun?.cases) ? selectedRun.cases : []),
    [selectedRun],
  );

  const selectedCase = useMemo(
    () => runCases.find((row) => String(row.id) === String(selectedCaseId)),
    [runCases, selectedCaseId],
  );

  const caseByCatalogId = useMemo(() => {
    const map = new Map();
    cases.forEach((row) => map.set(String(row.id), row));
    return map;
  }, [cases]);

  const selectedCatalog = useMemo(
    () =>
      cases.find((row) => String(row.id) === String(selectedCatalogId)) || null,
    [cases, selectedCatalogId],
  );

  const commentByRubricFromCatalog = useMemo(() => {
    const map = new Map();
    const catalog = selectedCase
      ? caseByCatalogId.get(String(selectedCase.testCaseId))
      : null;
    (Array.isArray(catalog?.rubrics) ? catalog.rubrics : []).forEach((row) => {
      if (row?.rubric_id) map.set(row.rubric_id, row.comment ?? '');
    });
    return map;
  }, [selectedCase, caseByCatalogId]);

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: 'auto' }}>
      <Stack spacing={2}>
        <Typography variant="h5">TESTS</Typography>
        <Typography variant="body2" color="text.secondary">
          Select catalog cases, run against live rubric rules, compare to each
          case&apos;s previous run. Load into TASKS carries task + answer only.
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            onClick={loadCases}
            disabled={casesLoading || runLoading}
          >
            Refresh cases
          </Button>
          <Button
            variant="contained"
            onClick={handleRun}
            disabled={runLoading || selectedIds.size === 0}
            startIcon={
              runLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {runLoading ? 'Running…' : `Run selected (${selectedIds.size})`}
          </Button>
          <Button
            variant="outlined"
            onClick={loadRuns}
            disabled={runsLoading || runLoading}
          >
            Refresh runs
          </Button>
        </Stack>
        {message && (
          <Typography
            variant="body2"
            color={
              message === 'Run failed' || message.startsWith('Failed')
                ? 'error.main'
                : 'success.main'
            }
          >
            {message}
          </Typography>
        )}

        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={
                        selectedIds.size > 0 && !allSelected
                      }
                      onChange={toggleAll}
                    />
                  </TableCell>
                  <TableCell>id</TableCell>
                  <TableCell>title</TableCell>
                  <TableCell>task</TableCell>
                  <TableCell>type</TableCell>
                  <TableCell>rubrics</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {cases.map((row) => {
                  const id = String(row.id);
                  const selected = id === String(selectedCatalogId);
                  const titleValue =
                    titleEdits[id] !== undefined
                      ? titleEdits[id]
                      : row.title || '';
                  return (
                    <TableRow
                      key={id}
                      hover
                      selected={selected}
                      onClick={() => selectCatalogCase(id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: selected
                          ? SELECTED_ROW_BG
                          : undefined,
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(id)}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => toggleOne(id)}
                        />
                      </TableCell>
                      <TableCell>{id}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            fullWidth
                            value={titleValue}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) =>
                              setTitleEdits((prev) => ({
                                ...prev,
                                [id]: event.target.value,
                              }))
                            }
                          />
                          <Button
                            size="small"
                            disabled={
                              titleEdits[id] === undefined ||
                              saveTitleId === id
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSaveTitle(id);
                            }}
                          >
                            Save
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell>{row.taskId}</TableCell>
                      <TableCell>{row.taskType}</TableCell>
                      <TableCell>
                        {Array.isArray(row.rubrics) ? row.rubrics.length : 0}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleLoadIntoTasks(row);
                          }}
                        >
                          Load TASKS
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!casesLoading && cases.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        No test cases yet — Make Test on TASKS after CHECK.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {selectedCatalog && (
          <>
            <Typography variant="h6">
              Test case #{selectedCatalog.id} detail
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Edit Expected / Comment like TASKS (Expected=true keeps frozen
              actual as etalon; false inverts PASS↔FAIL). Membership is fixed.
            </Typography>
            <TextField
              label="Answer"
              value={selectedCatalog.answerText || ''}
              fullWidth
              multiline
              minRows={4}
              InputProps={{ readOnly: true }}
            />
            <OutwriteCollapsibleTable
              title="Catalog rubrics"
              summary={`${(selectedCatalog.rubrics || []).length} rubrics`}
              defaultExpanded
              columns={[
                { key: 'rubric_id', label: 'rubric_id' },
                { key: 'actual', label: 'actual', width: 90 },
                { key: 'etalon', label: 'etalon', width: 90 },
                { key: 'expected', label: 'Expected', width: 100 },
                { key: 'comment', label: 'Comment' },
              ]}
            >
              {(selectedCatalog.rubrics || []).map((row, index) => {
                const draft = catalogAnnotations[row.rubric_id] || {
                  expectedOk: true,
                  comment: '',
                };
                const actual = String(row.actual || '').toUpperCase();
                const etalon =
                  draft.expectedOk !== false
                    ? actual || String(row.expected || '').toUpperCase()
                    : actual
                      ? invertPassFail(actual)
                      : invertPassFail(row.expected);
                return (
                  <TableRow key={`${row.rubric_id}-${index}`}>
                    <TableCell>{row.rubric_id}</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: verdictColor(
                          actual === 'PASS'
                            ? 'pass'
                            : actual === 'FAIL'
                              ? 'fail'
                              : '',
                        ),
                      }}
                    >
                      {row.actual || ''}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: verdictColor(
                          etalon === 'PASS'
                            ? 'pass'
                            : etalon === 'FAIL'
                              ? 'fail'
                              : '',
                        ),
                      }}
                    >
                      {etalon}
                    </TableCell>
                    <TableCell padding="checkbox" align="center">
                      <Checkbox
                        checked={draft.expectedOk !== false}
                        onChange={(event) =>
                          handleCatalogExpected(
                            row.rubric_id,
                            event.target.checked,
                          )
                        }
                        inputProps={{
                          'aria-label': `Expected for ${row.rubric_id}`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={4}
                        value={draft.comment ?? ''}
                        onChange={(event) =>
                          handleCatalogComment(
                            row.rubric_id,
                            event.target.value,
                          )
                        }
                        placeholder="optional"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {(selectedCatalog.rubrics || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No rubrics on this test case.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </OutwriteCollapsibleTable>
            {(selectedCatalog.rubrics || []).length > 0 && (
              <Box>
                <Button
                  variant="outlined"
                  onClick={handleSaveCatalogAnnotations}
                  disabled={catalogSaveLoading}
                  startIcon={
                    catalogSaveLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : null
                  }
                >
                  {catalogSaveLoading
                    ? 'Saving…'
                    : 'Save Expected / Comment'}
                </Button>
              </Box>
            )}
          </>
        )}

        <Typography variant="h6">Run history</Typography>
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 240 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>id</TableCell>
                  <TableCell>started</TableCell>
                  <TableCell>status</TableCell>
                  <TableCell>pass</TableCell>
                  <TableCell>fail</TableCell>
                  <TableCell>inconsistent</TableCell>
                  <TableCell>duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((row) => {
                  const id = String(row.id);
                  const selected = id === String(selectedRunId);
                  return (
                    <TableRow
                      key={id}
                      hover
                      selected={selected}
                      onClick={() => setSelectedRunId(id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: selected
                          ? SELECTED_ROW_BG
                          : undefined,
                      }}
                    >
                      <TableCell>{id}</TableCell>
                      <TableCell>
                        {row.startedAt
                          ? new Date(row.startedAt).toLocaleString()
                          : ''}
                      </TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.passCount}</TableCell>
                      <TableCell>{row.failCount}</TableCell>
                      <TableCell>{row.inconsistentCount}</TableCell>
                      <TableCell>
                        {row.durationMs != null
                          ? `${Math.round(row.durationMs / 1000)}s`
                          : ''}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!runsLoading && runs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        No runs yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {runDetailLoading && (
          <CircularProgress size={24} />
        )}

        {selectedRun && (
          <>
            <Typography variant="h6">
              Run #{selectedRun.id} cases
            </Typography>
            <Paper variant="outlined">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>case</TableCell>
                      <TableCell>title</TableCell>
                      <TableCell>task</TableCell>
                      <TableCell>verdict</TableCell>
                      <TableCell>previous</TableCell>
                      <TableCell>delta</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runCases.map((row) => {
                      const id = String(row.id);
                      const selected = id === String(selectedCaseId);
                      const prev = row.previousVerdict || '—';
                      const delta =
                        row.previousVerdict &&
                        row.previousVerdict !== row.verdict
                          ? `${row.previousVerdict} → ${row.verdict}`
                          : row.previousVerdict
                            ? 'same'
                            : 'first';
                      const catalog = caseByCatalogId.get(
                        String(row.testCaseId),
                      );
                      return (
                        <TableRow
                          key={id}
                          hover
                          selected={selected}
                          onClick={() => setSelectedCaseId(id)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: selected
                              ? SELECTED_ROW_BG
                              : undefined,
                          }}
                        >
                          <TableCell>{row.testCaseId}</TableCell>
                          <TableCell>
                            {row.title || catalog?.title || ''}
                          </TableCell>
                          <TableCell>
                            {row.taskId || catalog?.taskId || ''}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: verdictColor(row.verdict),
                            }}
                          >
                            {row.verdict}
                          </TableCell>
                          <TableCell
                            sx={{ color: verdictColor(row.previousVerdict) }}
                          >
                            {prev}
                          </TableCell>
                          <TableCell>{delta}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleLoadIntoTasks({
                                  taskId: row.taskId || catalog?.taskId,
                                  answerText:
                                    row.answerText ||
                                    catalog?.answerText ||
                                    '',
                                });
                              }}
                            >
                              Load TASKS
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {selectedCase && (
              <TextField
                label="Answer"
                value={
                  selectedCase.answerText ||
                  caseByCatalogId.get(String(selectedCase.testCaseId))
                    ?.answerText ||
                  ''
                }
                fullWidth
                multiline
                minRows={4}
                InputProps={{ readOnly: true }}
              />
            )}
            <OutwriteCollapsibleTable
              title="Rubric detail"
              summary={
                selectedCase
                  ? `${(selectedCase.rubrics || []).length} rows · ${selectedCase.verdict}`
                  : 'select a case'
              }
              defaultExpanded
              columns={[
                { key: 'rubric_id', label: 'rubric_id' },
                { key: 'expected', label: 'expected', width: 50 },
                { key: 'actual', label: 'actual', width: 50 },
                { key: 'status', label: 'status', width: 50 },
                { key: 'comment', label: 'Comment', width: 75 },
                { key: 'rule', label: 'rule' },
                { key: 'note', label: 'llm note' },
              ]}
            >
              {(selectedCase?.rubrics || []).map((row, index) => {
                const comment =
                  row.comment != null && row.comment !== ''
                    ? row.comment
                    : commentByRubricFromCatalog.get(row.rubric_id) || '';
                return (
                  <TableRow key={`${row.rubric_id}-${index}`}>
                    <TableCell>{row.rubric_id}</TableCell>
                    <TableCell>{row.expected || ''}</TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: verdictColor(
                          row.actual === 'PASS'
                            ? 'pass'
                            : row.actual === 'FAIL'
                              ? 'fail'
                              : '',
                        ),
                      }}
                    >
                      {row.actual || ''}
                    </TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                      {comment}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.rule || ''}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                      {row.note || ''}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!selectedCase && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      Select a case row above.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </OutwriteCollapsibleTable>
            {selectedCase?.errorSummary && (
              <Typography variant="body2" color="error.main">
                {selectedCase.errorSummary}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}
